<template>
  <div class="spl-edit-template__step spl-book-address">
    <!-- *********************** Contact  *********************************** -->
    <contact-component
      :parcel-data="bookingData.book_parcel_model"
      :is-selecting="true"
      @contactSelected="contactSet" />
    <span v-if="contactRequired && bookingData.book_parcel_model.Contact.Contact_ID === -1" class="error">Please select a Contact</span>
    <br>
    <!-- *********************** Addresses  *********************************** -->
    <div class="row">
      <!-- *********************** Pickup Address  *********************************** -->
      <div class="col10">
        <form-label>Pickup Address</form-label>

        <form-filtered-select
          ref="pickupAddress"
          v-model="bookingData.pickup_address_object"
          :options="found_pickup_addresses"
          :selected-key="bookingData.book_parcel_model.Pickup_Address_ID"
          :invalid="$v.bookingData.book_parcel_model.Pickup_Address_ID.$anyError"
          :internal-search="false"
          :class="[{'spl-singleselect-valid': bookingData.book_parcel_model.Pickup_Address_ID > 0}, {'spl-singleselect-invalid': bookingData.book_parcel_model.Pickup_Address_ID <= 0}]"
          placeholder="Search for Addresses..."
          class="spl-book-address__pickup-address"
          icon="search"
          no-options="Type at least 3 characters to start search"
          @search-change="getAddresses($event, 'pickup')"
          @updateDropdownValue="update_pickup_address" />
        <span v-if="$v.bookingData.book_parcel_model.Pickup_Address_ID.$error" class="error">Pickup Address is required</span>
        <div class="spl-text__formatted">
          {{ bookingData.pickup_address_formatted }}
        </div>
      </div>
      <div class="col1">
        <form-label />
        <form-button @onClick="show_Create_Pickup_Address_Clicked">
          New
        </form-button>
        <edit-create-address-modal
          :address-model="bookingData.pickup_address_object"
          :customer-key="bookingData.book_parcel_model.Customer_ID"
          :is-visible="show_create_pickup_address"
          @close="close_Address"
          @address_Created="pickup_Address_Created" />
      </div>
    </div>
    <div class="row">
      <!-- *********************** Delivery Address  *********************************** -->
      <div class="col10">
        <form-label>Delivery Address</form-label>
        <form-filtered-select
          ref="deliveryAddress"
          v-model="bookingData.delivery_address_object"
          :selected-key="bookingData.book_parcel_model.Delivery_Address_ID"
          :options="found_delivery_addresses"
          :internal-search="false"
          :invalid="$v.bookingData.book_parcel_model.Delivery_Address_ID.$anyError"
          :class="[{'spl-singleselect-valid': bookingData.book_parcel_model.Delivery_Address_ID > 0}, {'spl-singleselect-invalid': bookingData.book_parcel_model.Delivery_Address_ID <= 0}]"
          placeholder="Search for Addresses..."
          icon="search"
          no-options="Type at least 3 characters to start search"
          class="spl-book-address__delivery-address"
          @search-change="getAddresses($event, 'delivery')"
          @updateDropdownValue="update_delivery_address" />
        <span v-if="$v.bookingData.book_parcel_model.Delivery_Address_ID.$error" class="error">Delivery Address is required</span>
        <!-- *********************** Show Delivery Address  *********************************** -->
        <div class="spl-text__formatted">
          {{ bookingData.delivery_address_formatted }}
        </div>
      </div>
      <div class="col1">
        <form-label />
        <form-button @onClick="show_Create_Delivery_Address_Clicked">
          New
        </form-button>
        <edit-create-address-modal
          :address-model="bookingData.delivery_address_object"
          :customer-key="bookingData.book_parcel_model.Customer_ID"
          :country-key="bookingData.book_parcel_model.Country_ID"
          :is-visible="show_create_delivery_address"
          @close="close_Address"
          @address_Created="delivery_Address_Created" />
      </div>
    </div>

    <!-- Next step footer -->
    <general-footer
      :cancel-visible="$mq === 'sm'"
      :positive-label="nextStepLabel"
      negative-label="Back"
      @negativeClicked="previousStep"
      @positiveClicked="nextStep" />
  </div>
</template>

<script>
import Helpers from '@/helpers.vue'
import webApi from '@/webApi'
import { debounce } from 'lodash'
import { integer, required, minValue } from 'vuelidate/lib/validators'
import { mapActions } from 'vuex'

export default {
  name: 'BookingAddress',
  mixins: [Helpers],
  props: ['bookingData', 'nextStepLabel'],
  data () {
    return {
      add_new_contact: false,
      found_pickup_addresses: [],
      found_delivery_addresses: [],
      show_create_pickup_address: false,
      show_create_delivery_address: false,
      deliveryAddress: {},
      currentSearchDebounce: null,
      currentProfileDebounce: null,
      address_id: [],
      contactRequired: false
    }
  },
  validations: {
    bookingData: {
      book_parcel_model: {
        Delivery_Address_ID: {
          required,
          integer,
          minValue: minValue(0)
        },
        Pickup_Address_ID: {
          required,
          integer,
          minValue: minValue(0)
        }
      }
    }
  },
  watch: {
    bookingData: {
      handler: function () {
        this.$forceUpdate()
      },
      deep: true
    }
  },
  mounted () {
    this.$watch(vm => [vm.bookingData.book_parcel_model.Country_ID, vm.bookingData.book_parcel_model.Customer_ID, vm.bookingData.book_parcel_model.Is_Forward], val => {
      if (this.bookingData.book_parcel_model.Country_ID !== -1 && this.bookingData.book_parcel_model.Customer_ID !== -1) {
        this.getProfiler(this.bookingData.book_parcel_model.Customer_ID, this.bookingData.book_parcel_model.Country_ID, this.bookingData.book_parcel_model.Is_Forward)
      }
    },
    {
      immediate: true,
      deep: true
    })
  },
  methods: {
    ...mapActions('profiler', ['loadProfiler']),
    reset (isForward) {
      if (isForward) {
        this.$refs.deliveryAddress.selected = null
      } else {
        this.$refs.pickupAddress.selected = null
      }

      this.add_new_contact = false
      this.found_delivery_addresses = []
      this.found_pickup_addresses = []
      this.show_create_pickup_address = false
      this.show_create_delivery_address = false
    },
    getProfiler (customer_id, country_id, is_forward) {
      var profileFilter = { Customer_ID: customer_id, Country_ID: country_id, Is_Forward: is_forward }
      this.loadProfiler(profileFilter).then(() => {
        this.profileData = JSON.parse(window.sessionStorage.getItem('Profiler_List'))
        if (this.profileData !== null && this.profileData.Fields.length > 0) {
          this.address_id = []
          const delivery = this.profileData.Fields.find(el => el.Name === 'DELIVERY ADDRESS')
          const contact = this.profileData.Fields.find(el => el.Name === 'CONTACT')
          if (delivery) {
            this.address_id = delivery.Options
            this.startAddressSearch('', 'delivery')
            if (this.currentProfileDebounce) {
              this.currentProfileDebounce.cancel()
            }
            this.currentProfileDebounce = debounce(this.setDefaultValue, 300)
            this.currentProfileDebounce(delivery)
          }
          if (contact) {
            this.contactRequired = contact.Required
          }
        }
      }).catch((error) => {
        window.bus.$emit('openErrorModal', error.ExceptionMessage)
      })
    },
    setDefaultValue (delivery) {
      if (delivery.Default_Value !== '' && this.bookingData.book_parcel_model.Delivery_Address_ID === -1 &&
        this.found_delivery_addresses.length > 0) {
        this.bookingData.book_parcel_model.Delivery_Address_ID = parseInt(delivery.Default_Value)
        var address_object = this.found_delivery_addresses.find(address => address.value === parseInt(delivery.Default_Value))
        this.update_delivery_address(address_object)
      }
    },
    // Set the contact
    contactSet (item) {
      this.bookingData.book_parcel_model.Contact.Contact_ID = item.value
      this.bookingData.book_parcel_model.Contact.Contact_Name = ''
      this.bookingData.book_parcel_model.Contact.Phone_Number = ''
      this.add_new_contact = false
    },

    newContact () {
      this.bookingData.book_parcel_model.Contact.Contact_ID = -1
      this.add_new_contact = true
    },
    // ************************ Searches for the addresses through the api ************************
    getAddresses (search, type) {
      if (this.currentSearchDebounce) {
        this.currentSearchDebounce.cancel()
      }
      this.currentSearchDebounce = debounce(this.startAddressSearch, 300)
      this.currentSearchDebounce(search, type)
    },

    startAddressSearch (search, type) {
      if (search === '' && this.address_id.length === 0) {
        return
      }
      var countryID = this.bookingData.book_parcel_model.Country_ID
      var isForward = this.bookingData.book_parcel_model.Is_Forward
      // Addressmodel for the search
      var addressSearchModel = {
        Customer_ID: this.bookingData.book_parcel_model.Customer_ID,
        Search_Text: search,
        Maximum_Number_Of_Rows: 10,
        Address_ID: this.address_id,
        Type: type
      }
      if ((isForward && type === 'delivery') || (!isForward && type === 'pickup')) {
        addressSearchModel.Country_ID = countryID
      }

      webApi.address.get_Address_List(addressSearchModel)
        .then((response) => {
          if (type === 'delivery') {
            this.found_delivery_addresses = []
          } else {
            this.found_pickup_addresses = []
          }
          var responseAddress = response.data
          responseAddress.forEach(address => {
            // Build the  string shown in the dropdown
            this.addFoundAddress(address, type)
          })
        }).catch((response) => {
          window.bus.$emit('openErrorModal', 'error loading event info: ' + response.data.ExceptionMessage)
        })
    },

    addFoundAddress (address, type) {
      var addressLabel =
              address.Name_1.substring(0, 40) +
              ', ' +
              address.Street_1.substring(0, 40) +
              ', ' +
              address.Postal_Code.substring(0, 20) +
              ', ' +
              address.City.substring(0, 20) +
              ', ' +
              address.Country_Name.substring(0, 20)

      var addressObject = {
        label: addressLabel,
        value: address.Address_ID,
        address_object: address
      }
      if (type === 'delivery') {
        this.found_delivery_addresses.push(addressObject)
      } else {
        this.found_pickup_addresses.push(addressObject)
      }
    },
    // ************************ New Pickup address was chosen from dopdown ************************
    update_pickup_address (addressItem) {
      if (!addressItem) return
      this.bookingData.book_parcel_model.Pickup_Address_ID = addressItem.value
      this.bookingData.pickup_address_object = {
        Pickup_Address_ID: addressItem.value,
        Name_1: addressItem.address_object.Name_1.substring(0, 75),
        Street_1: addressItem.address_object.Street_1,
        Postal_Code: addressItem.address_object.Postal_Code,
        City: addressItem.address_object.City,
        Country_Name: addressItem.address_object.Country_Name
      }

      this.bookingData.pickup_address_formatted = ''
      this.bookingData.pickup_address_formatted += '\n' + this.bookingData.pickup_address_object.Name_1
      this.bookingData.pickup_address_formatted += '\n' + this.bookingData.pickup_address_object.Street_1
      this.bookingData.pickup_address_formatted += '\n' + this.bookingData.pickup_address_object.Postal_Code + ' ' + this.bookingData.pickup_address_object.City
      this.bookingData.pickup_address_formatted += '\n' + this.bookingData.pickup_address_object.Country_Name
    },
    // ************************ New delivey address was chosen from dopdown ************************
    update_delivery_address (addressItem) {
      if (!addressItem) return
      this.bookingData.book_parcel_model.Delivery_Address_ID = addressItem.value
      this.bookingData.delivery_address_object = {
        Delivery_Address_ID: addressItem.value,
        Name_1: addressItem.address_object.Name_1.substring(0, 75),
        Street_1: addressItem.address_object.Street_1,
        Postal_Code: addressItem.address_object.Postal_Code,
        City: addressItem.address_object.City,
        Country_Name: addressItem.address_object.Country_Name
      }

      this.bookingData.delivery_address_formatted = ''
      this.bookingData.delivery_address_formatted += '\n' + this.bookingData.delivery_address_object.Name_1
      this.bookingData.delivery_address_formatted += '\n' + this.bookingData.delivery_address_object.Street_1
      this.bookingData.delivery_address_formatted += '\n' + this.bookingData.delivery_address_object.Postal_Code + ' ' + this.bookingData.delivery_address_object.City
      this.bookingData.delivery_address_formatted += '\n' + this.bookingData.delivery_address_object.Country_Name
    },
    //* ****************************************** Create Address Functions *************************************
    // Show the Create Address modal
    show_Create_Delivery_Address_Clicked () {
      this.show_create_delivery_address = true
    },
    // Show the Create Address modal
    show_Create_Pickup_Address_Clicked () {
      this.show_create_pickup_address = true
    },
    close_Address () {
      this.show_create_delivery_address = false
      this.show_create_pickup_address = false
    },
    pickup_Address_Created (addressModel) {
      var addressItem = {
        value: addressModel.Address_ID,

        address_object: {
          Name_1: addressModel.Name_1.substring(0, 75),
          Street_1: addressModel.Street_1,
          Postal_Code: addressModel.Postal_Code,
          City: addressModel.City,
          Country_Name: addressModel.Country_Name,
          Address_ID: addressModel.Address_ID
        }
      }

      this.update_pickup_address(addressItem)
      this.show_create_pickup_address = false
      this.addFoundAddress(addressItem.address_object, 'pickup')
      this.$forceUpdate()
    },
    delivery_Address_Created (addressModel) {
      var addressItem = {
        value: addressModel.Address_ID,
        address_object: {
          Name_1: addressModel.Name_1.substring(0, 75),
          Street_1: addressModel.Street_1,
          Postal_Code: addressModel.Postal_Code,
          City: addressModel.City,
          Country_Name: addressModel.Country_Name,
          Address_ID: addressModel.Address_ID
        }
      }

      this.update_delivery_address(addressItem)
      this.show_create_delivery_address = false
      this.addFoundAddress(addressItem.address_object, 'delivery')
      this.$forceUpdate()
    },
    nextStep () {
      this.$v.bookingData.book_parcel_model.Pickup_Address_ID.$touch()
      this.$v.bookingData.book_parcel_model.Delivery_Address_ID.$touch()
      if (!this.$v.$invalid && !(this.contactRequired && this.bookingData.book_parcel_model.Contact.Contact_ID === -1)) {
        this.$emit('nextStep', this.bookingData)
      }
    },
    isValid () {
      return !this.$v.$invalid
    },
    previousStep () {
      this.$emit('previousStep', this.bookingData)
    }
  }
}
</script>

<style lang="scss">
@import '../../variables.scss';

.spl-edit-template__step {
  & .spl-text__formatted {
    padding-bottom: 40px;
  }
}

@include mobile() {
  .spl-book-address {
    & .spl-button {
      height: 40px;
      font-size: 14px;
    }
    &__contact {
      .col6 {
        float: none;
      }
      & .spl-button {
        width: auto;
        margin: 10px 0 0 10px;
      }
    }
    &__pickup-address,
    &__delivery-address {
      & .multiselect__option {
        white-space: normal;
      }

      & .multiselect__option--highlight:after {
        display: none;
      }
    }
    & .spl-text__formatted {
      display: none;
    }
  }
}

</style>
